import {
  ActionError,
  Button,
  EmptyState,
  FilterSegments,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  plural,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { formatDocNumber } from '@/features/clinic-profile/doc-number'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import { useClinics, type AdminClinic, type ClinicFilter } from './api'
import { clinicName, clinicStatus } from './labels'

/**
 * Clínicas: o painel da Westhetics (docs/areas/administracao.md, "Clínicas"; RN-ADM-53 e 85).
 * Arquétipo: lista de trabalho, só de root, sem depender de clínica ativa. Abre em "Em implantação"
 * enquanto houver alguma: é o trabalho que ainda depende da Westhetics ou da gestora.
 */

const FILTERS: ClinicFilter[] = ['onboarding', 'active', 'blocked', 'no_network']

const EMPTY_FILTER: Record<ClinicFilter, { title: string; description: string }> = {
  onboarding: {
    title: 'Nenhuma clínica em implantação',
    description: 'Toda clínica ativa já concluiu as quatro etapas e pode emitir documentos.',
  },
  active: {
    title: 'Nenhuma clínica ativa',
    description: 'As clínicas com o acesso suspenso continuam no filtro Bloqueadas.',
  },
  blocked: {
    title: 'Nenhuma clínica bloqueada',
    description:
      'Bloquear suspende o acesso de todo mundo da clínica e os links dos pacientes. As bloqueadas aparecem aqui.',
  },
  no_network: {
    title: 'Nenhuma clínica sem rede',
    description: 'Toda clínica cadastrada já pertence a uma rede.',
  },
}

export function ClinicsPage() {
  const navigate = useNavigate()
  const session = useSession()
  // "entrar" na linha: troca a clínica ativa sem passar pelo detalhe (RN-AUT-11, RN-ADM-85).
  const [entering, setEntering] = useState<string | null>(null)
  const [enterError, setEnterError] = useState<unknown>(null)

  const stored = useStoredFilter('clinics', FILTERS, 'active')
  const { setFilter } = stored
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounced(search.trim())
  // A tela abre no que tem pendência (RN-ADM-53), até a pessoa escolher um segmento: o número de
  // clínicas em implantação vem na primeira resposta, e a lista troca sozinha para ele.
  const [onboardingSeen, setOnboardingSeen] = useState(0)
  const filter: ClinicFilter =
    !stored.hasChosen && onboardingSeen > 0 ? 'onboarding' : stored.filter
  const query = useClinics({ filter, search: debouncedSearch || undefined, page })

  /** Root passa a agir como gestora da clínica e segue para o dashboard, ou para a implantação. */
  async function enter(clinic: AdminClinic) {
    setEntering(clinic.id)
    setEnterError(null)
    try {
      await session.switchClinic(clinic.id)
      navigate(clinic.onboarding.isCompleted ? routes.dashboard : routes.onboarding)
    } catch (error) {
      setEnterError(error)
      setEntering(null)
    }
  }
  const counts = query.data?.meta.counts
  const onboardingCount = counts?.onboarding ?? 0
  if (onboardingCount !== onboardingSeen) setOnboardingSeen(onboardingCount)

  // `active` conta toda clínica não bloqueada, em implantação ou não.
  const total = (counts?.active ?? 0) + (counts?.blocked ?? 0)

  const options: FilterSegmentOption<ClinicFilter>[] = [
    { value: 'onboarding', label: 'Em implantação', count: counts?.onboarding },
    { value: 'active', label: 'Ativas', count: counts?.active },
    { value: 'blocked', label: 'Bloqueadas', count: counts?.blocked },
    { value: 'no_network', label: 'Sem rede', count: counts?.no_network },
  ]

  const clinics = query.data?.data ?? []
  const hasNoClinic = !query.isPending && total === 0
  const showSearch = total > 20 || search !== ''

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Westhetics' }, { label: 'Clínicas' }]}
        actions={
          <Button size="sm" asChild>
            <Link to={routes.admin.newClinic}>+ Nova clínica</Link>
          </Button>
        }
      />
      <Page wide>
        <PageTitle
          title="Clínicas"
          subtitle={
            counts
              ? `${plural(total, 'clínica')} · ${
                  onboardingCount > 0 ? onboardingCount : 'nenhuma'
                } em implantação`
              : 'Toda clínica que usa o sistema, da criação à implantação concluída.'
          }
        />

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar as clínicas"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : hasNoClinic ? (
          <EmptyState
            className="mt-6"
            title="Nenhuma clínica ainda"
            description="Cada clínica nasce aqui, com a primeira gestora. O resto ela mesma completa na implantação: testemunhas e procedimentos."
            action={
              <Button className="max-desk:min-h-touch" asChild>
                <Link to={routes.admin.newClinic}>Criar a primeira clínica</Link>
              </Button>
            }
          />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <FilterSegments
                label="Filtrar clínicas"
                options={options}
                value={filter}
                onChange={(value) => {
                  setFilter(value)
                  setPage(1)
                }}
              />
              {showSearch ? (
                <SearchInput
                  className="tablet:max-w-[320px]"
                  value={search}
                  onChange={(value) => {
                    setSearch(value)
                    setPage(1)
                  }}
                  placeholder="Buscar por nome ou documento"
                  aria-label="Buscar clínica"
                />
              ) : null}
            </div>

            {enterError ? (
              <ActionError
                className="mt-4"
                title="Não foi possível entrar na clínica"
                description={errorMessage(enterError)}
                onDismiss={() => setEnterError(null)}
              />
            ) : null}

            <div className="mt-4">
              {query.isPending ? (
                <TableSkeleton rows={6} columns={6} />
              ) : clinics.length === 0 ? (
                <EmptyState
                  title={
                    debouncedSearch
                      ? `Nenhuma clínica para "${debouncedSearch}"`
                      : EMPTY_FILTER[filter].title
                  }
                  description={
                    debouncedSearch
                      ? 'A busca cobre a razão social, o nome fantasia e o documento, com ou sem pontuação, dentro do filtro escolhido.'
                      : EMPTY_FILTER[filter].description
                  }
                  action={
                    debouncedSearch ? (
                      <Button
                        className="max-desk:min-h-touch"
                        variant="outline"
                        onClick={() => setSearch('')}
                      >
                        Limpar busca
                      </Button>
                    ) : filter === 'active' ? (
                      <Button
                        className="max-desk:min-h-touch"
                        variant="outline"
                        onClick={() => setFilter('blocked')}
                      >
                        Ver bloqueadas
                      </Button>
                    ) : (
                      <Button
                        className="max-desk:min-h-touch"
                        variant="outline"
                        onClick={() => setFilter('active')}
                      >
                        Ver ativas
                      </Button>
                    )
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead>Clínica</TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead>Cidade/UF</TableHead>
                      <TableHead>Rede</TableHead>
                      <TableHead numeric>Gestoras</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead aria-label="Ações" />
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {clinics.map((clinic) => {
                      const status = clinicStatus(clinic)
                      return (
                        <TableRow
                          key={clinic.id}
                          onClick={() => navigate(routes.admin.clinic(clinic.id))}
                        >
                          <TableCell primary>
                            {/* Um bloco só: no cartão (abaixo de 900px) a razão social fica sob o nome. */}
                            <div>
                              {clinicName(clinic)}
                              {clinic.tradeName ? (
                                <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5] font-light">
                                  {clinic.legalName}
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell label="Documento" muted className="whitespace-nowrap">
                            {formatDocNumber(clinic.docNumber, clinic.docType)}
                          </TableCell>
                          <TableCell label="Cidade/UF" muted>
                            {clinic.address
                              ? `${clinic.address.city}/${clinic.address.state}`
                              : 'Sem endereço'}
                          </TableCell>
                          <TableCell label="Rede" muted>
                            {clinic.network?.name ?? 'Sem rede'}
                          </TableCell>
                          <TableCell label="Gestoras" numeric>
                            {clinic.counts.managers}
                          </TableCell>
                          <TableCell label="Status">
                            <StatusTag level={status.level}>{status.label}</StatusTag>
                          </TableCell>
                          <TableCell>
                            <RowActions>
                              <Link to={routes.admin.clinic(clinic.id)}>abrir</Link>
                              <button
                                type="button"
                                disabled={entering !== null}
                                onClick={() => void enter(clinic)}
                              >
                                {entering === clinic.id ? 'entrando…' : 'entrar'}
                              </button>
                            </RowActions>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
              {query.data && clinics.length > 0 ? (
                <ListFooter
                  meta={query.data.meta}
                  noun={['clínica', 'clínicas']}
                  note="em ordem de razão social"
                  onPage={setPage}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>
    </>
  )
}
